import type { FinanceAdminRow, FinanceDashboard, FinanceStage } from "../../api/admin";
import { ADMIN_PAGE_SIZE } from "../../api/admin";
import {
  Avatar,
  Badge,
  FilterSelect,
  KpiCard,
  Pager,
  SearchBox,
  StepDots,
} from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";
import { compactDate } from "../../lib/format";

const STAGES: [FinanceStage, string][] = [
  ["notStarted", "Not started"],
  ["inProgress", "Preparing"],
  ["ready", "Ready for referral"],
  ["referred", "Referred"],
];
const TIER: Record<string, "positive" | "warning" | "negative"> = {
  Ready: "positive",
  "Nearly Ready": "warning",
  "Needs Work": "negative",
};

export interface FinanceFilterState {
  search: string;
  stage: FinanceStage | "";
}

interface FinanceDashboardScreenProps {
  data: FinanceDashboard | null;
  loading: boolean;
  filter: FinanceFilterState;
  page: number;
  selectedId: string | null;
  idDocsTotal: number;
  moneyDocsTotal: number;
  onFilter: (patch: Partial<FinanceFilterState>) => void;
  onPage: (page: number) => void;
  onOpen: (row: FinanceAdminRow) => void;
}

/* "Device financing: laptop" reads as just "laptop" in a badge; make it "Laptop financing". */
function serviceLabel(service: string): string {
  const device = service.match(/^Device financing: (.+)$/);
  if (!device) return service;
  const thing = device[1] ?? "";
  return `${thing.charAt(0).toUpperCase()}${thing.slice(1)} financing`;
}

/* Fraction as a small bar, so "6/7 ready" reads at a glance. */
function Ready({ have, of }: { have: number; of: number }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-[#efe3d2]" aria-hidden="true">
        <i
          className={`block h-full ${have >= of ? "bg-[#4F7D3A]" : "bg-[#F99F1C]"}`}
          style={{ width: `${of ? (have / of) * 100 : 0}%` }}
        />
      </span>
      <span className="text-[12.5px] tabular">
        {have}/{of}
      </span>
    </span>
  );
}

/** Access to Finance: preparation and referrals. Only ticks and a tier exist; no personal financial data. */
export function FinanceDashboardScreen(props: FinanceDashboardScreenProps) {
  const { data, loading, filter, page, selectedId, onFilter, onPage, onOpen } = props;
  const filtered = Boolean(filter.search || filter.stage);

  const columns: Column<FinanceAdminRow>[] = [
    {
      key: "name",
      header: "Participant",
      sortValue: (r) => `${r.firstName} ${r.lastName}`.toLowerCase(),
      render: (r) => (
        <div className="flex items-center gap-3">
          <Avatar first={r.firstName} last={r.lastName} />
          <div className="min-w-0">
            <div className="truncate font-semibold">
              {`${r.firstName} ${r.lastName}`.trim() || "–"}
            </div>
            <div className="truncate text-[12px] text-text-muted">{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "progress",
      header: "Progress",
      sortValue: (r) => r.stepsDone.length,
      render: (r) => <StepDots done={r.stepsDone} label="Finance track" />,
    },
    {
      key: "services",
      header: "Services wanted",
      hideBelow: "lg",
      render: (r) =>
        r.services.length ? (
          <div className="flex max-w-[260px] flex-wrap gap-1">
            {r.services.map((s) => (
              <Badge key={s}>{serviceLabel(s)}</Badge>
            ))}
          </div>
        ) : (
          <span className="text-text-muted">–</span>
        ),
    },
    {
      key: "id",
      header: "ID docs",
      hideBelow: "md",
      sortValue: (r) => r.idDocsReady,
      render: (r) => <Ready have={r.idDocsReady} of={props.idDocsTotal} />,
    },
    {
      key: "money",
      header: "Money records",
      hideBelow: "md",
      sortValue: (r) => r.moneyDocsReady,
      render: (r) => <Ready have={r.moneyDocsReady} of={props.moneyDocsTotal} />,
    },
    {
      key: "tier",
      header: "Readiness",
      sortValue: (r) => r.tier,
      render: (r) =>
        r.tier ? (
          <Badge tone={TIER[r.tier] ?? "neutral"}>{r.tier}</Badge>
        ) : (
          <span className="text-text-muted">–</span>
        ),
    },
    {
      key: "handover",
      header: "Referred",
      hideBelow: "sm",
      sortValue: (r) => r.referredAt,
      render: (r) =>
        r.referredAt ? (
          <Badge tone="info">✓ {compactDate(r.referredAt)}</Badge>
        ) : (
          <span className="text-text-muted">Not yet</span>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <KpiCard label="Participants" value={data?.participants ?? "–"} tone="accent" />
        <KpiCard
          label="Referred to a provider"
          value={data?.referred ?? "–"}
          tone="info"
          hint={
            data && data.participants
              ? `${Math.round((data.referred / data.participants) * 100)}% of participants`
              : undefined
          }
        />
        <KpiCard
          label="Ready, not yet referred"
          value={
            data
              ? data.rows.filter(
                  (r) => !r.referredAt && (r.tier === "Ready" || r.tier === "Nearly Ready"),
                ).length
              : "–"
          }
          tone="positive"
          hint="On this page: Ready or Nearly Ready"
        />
      </div>
      <p className="m-0 -mt-1 text-[12.5px] text-text-muted">
        Only checklists and a readiness tier are stored here. No ID numbers, documents or financial
        figures are collected.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={filter.search}
          onChange={(search) => onFilter({ search })}
          placeholder="Search name, email or ID"
        />
        <FilterSelect
          label="Stage"
          value={filter.stage}
          options={STAGES}
          onChange={(stage) => onFilter({ stage })}
        />
        {filtered ? (
          <button
            type="button"
            onClick={() => onFilter({ search: "", stage: "" })}
            className="h-10 px-2 text-[13px] font-semibold text-accent hover:underline"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <DataTable
        rows={data?.rows ?? []}
        columns={columns}
        rowKey={(r) => r.userId}
        onRowClick={onOpen}
        selectedKey={selectedId}
        loading={loading}
        rowLabel={(r) => `Open ${`${r.firstName} ${r.lastName}`.trim() || r.email}`}
        empty={
          filtered
            ? "No participants match these filters."
            : "No participants have started the finance track."
        }
      />
      <Pager page={page} total={data?.total ?? 0} size={ADMIN_PAGE_SIZE} onPage={onPage} />
    </div>
  );
}
